/**
 * Ticket 53, item 1: "Volume slider seems to do nothing on mobile. Adjusting or
 * even muting does not change volume when watching video stream on mobile."
 *
 * Output level was set by assigning to the audio element's `volume` property —
 * `RemoteAudioTrack.setVolume` does `el.volume = v` unless the track was built
 * with an AudioContext. **On iOS Safari that property is read-only**: the
 * assignment is accepted and ignored, so every position of the slider, mute
 * included, produced the same sound. Volume there belongs to the hardware
 * buttons unless the audio goes through Web Audio.
 *
 * Two things this deliberately does NOT do:
 *
 * 1. It does not turn on LiveKit's `webAudioMix`. That would route every remote
 *    track through Web Audio on every platform, and the library mutes the audio
 *    elements itself when it does. `useRoomAudio` hand-mutes elements in two
 *    places — the host's own OBS ingest (virtual loopback feedback) and the
 *    echo guard during a screen share — and both would silently stop
 *    suppressing anything. A desktop regression to fix a mobile bug.
 *
 * 2. It does not touch `el.muted`, for the same reason. That bit already has
 *    two owners and `applyEchoSuppression` writes it unconditionally, so a
 *    third writer would be overwritten by whichever ran last — and "the echo
 *    guard un-mutes a stream the viewer muted" is a worse bug than the one
 *    being fixed.
 *
 * `createMediaElementSource` is what makes that possible: it REDIRECTS the
 * element's own output into the graph rather than adding a second copy of it.
 * So there is nothing to mute, `el.muted` keeps its existing owners, and muting
 * the element still works — it silences the source feeding the graph.
 *
 * Where the plain property works (every desktop browser) none of this runs and
 * no AudioContext is ever created: `applyOutputVolume` takes exactly the path
 * it took before.
 */

/** The slice of a LiveKit RemoteAudioTrack this needs. Structural, so tests
 * can pass a plain object rather than half a WebRTC stack. */
export interface VolumeControllableTrack {
  setVolume: (volume: number) => void;
  attachedElements: HTMLMediaElement[];
}

export interface OutputGainGraph {
  ctx: AudioContext;
  gain: GainNode;
}

/** Keyed by element: one graph per playback element, built at most once. */
export type OutputGainGraphs = Map<HTMLMediaElement, OutputGainGraph>;

export interface OutputVolumeOptions {
  /** False on iOS Safari — see `elementVolumeIsHonoured`. */
  honoursElementVolume: boolean;
  graphs: OutputGainGraphs;
  /** Returns null when the browser has no Web Audio at all. */
  createContext: () => AudioContext | null;
  log?: (message: string) => void;
}

export const clampVolume = (volume: number): number => {
  if (!Number.isFinite(volume)) return 1;
  return Math.max(0, Math.min(1, volume));
};

/**
 * Whether assigning to `HTMLMediaElement.volume` actually does anything.
 *
 * The probe is the feature test, not a user-agent sniff: iOS is the browser
 * this is aimed at today, but "the volume property is read-only" is the
 * condition that matters and any engine may join or leave that set.
 */
export const elementVolumeIsHonoured = (doc: Document = document): boolean => {
  try {
    const probe = doc.createElement('audio');
    probe.volume = 0.5;
    return probe.volume === 0.5;
  } catch {
    return false;
  }
};

/**
 * The graph for one element, building it on first use.
 *
 * Lazy on purpose. `createMediaElementSource` cannot be undone — once an
 * element is routed into a graph it stays routed — so it is only ever called
 * for a viewer who has actually asked for a level the element cannot give
 * them. A viewer who never touches the slider keeps plain element playback.
 */
const gainGraphFor = (
  el: HTMLMediaElement,
  options: OutputVolumeOptions,
): OutputGainGraph | null => {
  const existing = options.graphs.get(el);
  if (existing) {
    // iOS suspends the context whenever it feels like it (backgrounding, a
    // call). Resuming here is safe: every caller is inside the input event
    // from the slider, which is the gesture the resume needs.
    if (existing.ctx.state === 'suspended') void existing.ctx.resume();
    return existing;
  }

  const ctx = options.createContext();
  if (!ctx) return null;

  try {
    const source = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    source.connect(gain);
    gain.connect(ctx.destination);
    if (ctx.state === 'suspended') void ctx.resume();
    const graph: OutputGainGraph = { ctx, gain };
    options.graphs.set(el, graph);
    options.log?.('[Room] OUTPUT_GAIN: engaged Web Audio for output volume');
    return graph;
  } catch (err: unknown) {
    // An element already routed into another graph throws InvalidStateError.
    // Falling back to the element path leaves the viewer exactly where they
    // were rather than silencing them.
    options.log?.(`[Room] OUTPUT_GAIN failed: ${String(err)}`);
    return null;
  }
};

/**
 * Sets the playback level for one remote audio track.
 *
 * Returns whether a gain graph carried it, which is what the tests assert on:
 * false means the ordinary element path, i.e. desktop behaviour unchanged.
 */
export const applyOutputVolume = (
  track: VolumeControllableTrack,
  volume: number,
  options: OutputVolumeOptions,
): boolean => {
  const level = clampVolume(volume);

  if (options.honoursElementVolume) {
    track.setVolume(level);
    return false;
  }

  let carried = false;
  for (const el of track.attachedElements) {
    const graph = gainGraphFor(el, options);
    if (!graph) continue;
    // `setTargetAtTime` rather than a bare assignment: an abrupt gain step on a
    // live stream is an audible click.
    graph.gain.gain.setTargetAtTime(level, graph.ctx.currentTime, 0.02);
    carried = true;
  }

  if (!carried) {
    // No Web Audio to be had. The element path is inert here, but it is also
    // all there is, and it keeps LiveKit's own bookkeeping honest.
    track.setVolume(level);
  }

  return carried;
};

/**
 * Drops every graph. Called when leaving the room: an AudioContext is a real
 * audio device handle, and iOS gives a page very few of them.
 */
export const releaseGainGraphs = (graphs: OutputGainGraphs): void => {
  for (const { ctx, gain } of graphs.values()) {
    try {
      gain.disconnect();
      void ctx.close();
    } catch {
      /* already torn down */
    }
  }
  graphs.clear();
};
